// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useDisclosure } from '@/hooks/use-disclosure';
import { Ticket } from '@/ticket/api/types';
import { TicketDescriptionDialog } from '@/ticket/components/dialogs/ticket-description-dialog';
import { TicketTitleDialog } from '@/ticket/components/dialogs/ticket-title-dialog';
import { TicketDescription } from '@/ticket/components/ticket-description';
import { TicketDetailAssetsAttached } from '@/ticket/components/ticket-detail-assets-attached';
import { TicketDetailProjectsAttached } from '@/ticket/components/ticket-detail-projects-attached';
import { TicketDetailsCard } from '@/ticket/components/ticket-details-card';
import { TicketHeader } from '@/ticket/components/ticket-header';
import { Stack } from '@mui/material';

interface Props {
  /**
   * The ticket to display details for
   */
  ticket: Ticket;
}

/**
 * Displays the main content of a ticket, including header, description,
 * details, and related items (assets/projects).
 */
export function TicketDetail({ ticket }: Props) {
  const titleDialog = useDisclosure(false);
  const descriptionDialog = useDisclosure(false);

  return (
    <Stack spacing={3}>
      {/* Title Dialog */}
      <TicketTitleDialog
        ticket={ticket}
        isOpen={titleDialog.isOpen}
        onClose={titleDialog.close}
      />

      {/* Description Dialog */}
      <TicketDescriptionDialog
        ticket={ticket}
        isOpen={descriptionDialog.isOpen}
        onClose={descriptionDialog.close}
      />

      {/* Header */}
      <TicketHeader ticket={ticket} onEditTitle={titleDialog.open} />

      {/* Description */}
      <TicketDescription
        description={ticket.description}
        onEditDescription={descriptionDialog.open}
      />

      {/* Details */}
      <TicketDetailsCard ticket={ticket} />

      {/* Assets and Projects */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        flex={1}
        justifyContent="space-between"
      >
        <TicketDetailAssetsAttached ticket={ticket} />
        <TicketDetailProjectsAttached ticket={ticket} />
      </Stack>
    </Stack>
  );
}
